// utils
import { filter, sample } from "lodash";
import { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  TextField,
  Modal,
  Box,
  Fade,
  Backdrop,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { db, auth, app, functions } from "../Firebase/firebase";
import {
  collection as firestoreCollection,
  addDoc,
  doc as firestoreDoc,
  updateDoc as firestoreUpdateDoc,
} from "firebase/firestore";
import Page from "../components/Page";
import Label from "../components/Label";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import {
  UserListHead,
  UserListToolbar,
  UserMoreMenu,
} from "../components/_dashboard/user";
import { mockImgAvatar } from "../utils/mockImages";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "trackingId", label: "Tracking ID", alignRight: false },
  { id: "productName", label: "Product Name", alignRight: false },
  { id: "customerName", label: "Customer", alignRight: false },
  { id: "orderDate", label: "Order Date", alignRight: false },
  { id: "trackingStops", label: "Tracking Stops", alignRight: false },
  { id: "" },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_product) => {
      const searchTerm = query.toLowerCase();
      return (
        _product.productName?.toLowerCase().indexOf(searchTerm) !== -1 ||
        _product.customerName?.toLowerCase().indexOf(searchTerm) !== -1 ||
        _product.trackingId?.toLowerCase().indexOf(searchTerm) !== -1
      );
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function User() {
  const [currentUser, setCurrentUser] = useState();
  const [adminToken, setAdminToken] = useState();
  const [productsList, setProductsList] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        navigate("/login");
      }
    });
  }, [navigate]);

  useEffect(() => {
    const unSubscribe = onSnapshot(collection(db, "products"), (snapshot) => {
      setProductsList(
        snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    });
    return unSubscribe;
  }, []);

  const products = productsList.map((product) => ({
    trackingId: product?.trackingId,
    productName: product?.productName,
    customerName: product?.customerName,
    orderDate: product?.orderDate,
    trackingStops: product?.trackingStops,
    id: product?.id,
  }));

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("trackingId");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    productName: "",
    customerName: "",
    orderDate: "",
  });
  const [trackingStops, setTrackingStops] = useState([]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = products.map((n) => n.trackingId);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - products.length) : 0;

  const filteredProducts = applySortFilter(
    products,
    getComparator(order, orderBy),
    filterName
  );

  const isProductNotFound = filteredProducts.length === 0;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied");
  };

  // CRUD Operations for Products
  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteDoc(doc(db, "products", productId));
        alert("Product deleted successfully!");
      } catch (error) {
        console.error("Error deleting product:", error);
        alert("Failed to delete product. Please try again.");
      }
    }
  };

  const handleEditProduct = (productId) => {
    navigate(`/dashboard/products/edit/${productId}`);
  };

  const handleViewProduct = (productId) => {
    navigate(`/dashboard/products/view/${productId}`);
  };

  // Modal handlers
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setNewProduct({
      productName: "",
      customerName: "",
      orderDate: "",
    });
    setTrackingStops([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTrackingStopInputChange = (index, field, value) => {
    setTrackingStops((prev) =>
      prev.map((stop, i) => (i === index ? { ...stop, [field]: value } : stop))
    );
  };

  const handleAddTrackingStop = () => {
    setTrackingStops((prev) => [
      ...prev,
      { activity: "", datetime: "", location: "" },
    ]);
  };

  const handleRemoveTrackingStop = (index) => {
    setTrackingStops((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper functions for datetime formatting
  const formatDateTimeForInput = (dateTimeString) => {
    // Convert "30th Sep, 2025, 14:00" back to "2025-09-30T14:00" for datetime-local input
    if (!dateTimeString || typeof dateTimeString !== "string") return "";

    // Parse the formatted string and convert to ISO format for datetime-local
    const parts = dateTimeString.match(
      /(\d+)(?:st|nd|rd|th)?\s+(\w+),\s+(\d+),\s+(\d+):(\d+)/
    );
    if (!parts) return "";

    const [, day, month, year, hour, minute] = parts;
    const monthNames = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };

    const monthNum = monthNames[month];
    if (!monthNum) return "";

    return `${year}-${monthNum}-${day.padStart(2, "0")}T${hour}:${minute}`;
  };

  const formatDateTimeForDisplay = (dateTimeValue) => {
    // Convert "2025-09-30T14:00" to "30th Sep, 2025, 14:00"
    if (!dateTimeValue) return "";

    const date = new Date(dateTimeValue);
    if (isNaN(date.getTime())) return "";

    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hour = date.getHours().toString().padStart(2, "0");
    const minute = date.getMinutes().toString().padStart(2, "0");

    // Add ordinal suffix to day
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
        ? "rd"
        : "th";

    return `${day}${suffix} ${month}, ${year}, ${hour}:${minute}`;
  };

  const handleAddProduct = async () => {
    // Validate required fields
    if (
      !newProduct.productName ||
      !newProduct.customerName ||
      !newProduct.orderDate
    ) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate tracking stops
    if (trackingStops.length === 0) {
      alert("Please add at least one tracking stop");
      return;
    }

    // Validate each tracking stop has all required fields
    for (let i = 0; i < trackingStops.length; i++) {
      const stop = trackingStops[i];
      if (!stop.activity || !stop.datetime || !stop.location) {
        alert(`Please fill in all fields for Tracking Stop ${i + 1}`);
        return;
      }

      // Validate field types (should be non-empty strings)
      if (typeof stop.activity !== "string" || stop.activity.trim() === "") {
        alert(
          `Activity field in Tracking Stop ${i + 1} must be a non-empty string`
        );
        return;
      }
      if (typeof stop.location !== "string" || stop.location.trim() === "") {
        alert(
          `Location field in Tracking Stop ${i + 1} must be a non-empty string`
        );
        return;
      }
      if (typeof stop.datetime !== "string" || stop.datetime.trim() === "") {
        alert(
          `Date & Time field in Tracking Stop ${
            i + 1
          } must be a non-empty string`
        );
        return;
      }
    }

    try {
      // Create the product data structure (without trackingId - will be auto-generated)
      const trackingStopsData = [];

      // Add custom tracking stops if provided
      trackingStops.forEach((stop) => {
        if (stop.activity && stop.datetime && stop.location) {
          trackingStopsData.push({
            activity: stop.activity.trim(),
            location: stop.location.trim(),
            datetime: stop.datetime.trim(),
          });
        }
      });

      const productData = {
        productName: newProduct.productName,
        customerName: newProduct.customerName,
        orderDate: newProduct.orderDate,
        trackingStops: trackingStopsData,
      };

      // Use .add() to auto-generate document ID and get the reference
      const docRef = await addDoc(firestoreCollection(db, "products"), {
        ...productData,
      });

      // Generate tracking ID using the auto-generated document ID
      const trackingId = `TRK${docRef.id.slice(-8).toUpperCase()}`;

      // Update the document with the generated tracking ID
      await firestoreUpdateDoc(firestoreDoc(db, "products", docRef.id), {
        trackingId,
      });

      // Close modal and reset form
      handleCloseModal();
      alert(`Product added successfully! Tracking ID: ${trackingId}`);
    } catch (error) {
      console.error("Error adding product:", error);
      alert("Failed to Add Shipping. Please try again.");
    }
  };

  return (
    <div>
      <Page title="User | Dashboard">
        <Container>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Typography variant="h4" gutterBottom>
              Shipments
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <TextField
                placeholder="Search shipments..."
                value={filterName}
                onChange={handleFilterByName}
                variant="outlined"
                size="small"
              />
              <Button variant="contained" onClick={handleOpenModal}>
                Add Shipping
              </Button>
            </Stack>
          </Stack>

          <Card>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={products.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredProducts
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row) => {
                        const {
                          trackingId,
                          productName,
                          customerName,
                          orderDate,
                          trackingStops,
                          id,
                        } = row;
                        const isItemSelected =
                          selected.indexOf(trackingId) !== -1;

                        return (
                          <TableRow
                            hover
                            key={trackingId}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell align="left">{trackingId}</TableCell>
                            <TableCell align="left">{productName}</TableCell>
                            <TableCell align="left">{customerName}</TableCell>
                            <TableCell align="left">{orderDate}</TableCell>
                            <TableCell align="left">
                              {trackingStops && trackingStops.length > 0
                                ? `${trackingStops.length} stop${
                                    trackingStops.length > 1 ? "s" : ""
                                  }`
                                : "No stops"}
                            </TableCell>
                            <TableCell align="right">
                              <UserMoreMenu
                                uid={id}
                                trackingId={trackingId}
                                productName={productName}
                                customerName={customerName}
                                orderDate={orderDate}
                                trackingStops={trackingStops}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={7} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isProductNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={products.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </Container>

        {/* Modal for Adding New Product */}
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={modalOpen}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "90%",
                maxWidth: 400,
                bgcolor: "background.paper",
                border: "none",
                borderRadius: "5px",
                boxShadow: 24,
                p: 4,
                maxHeight: "90vh",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" component="h2" gutterBottom>
                Add New Product
              </Typography>
              <TextField
                label="Product Name"
                name="productName"
                value={newProduct.productName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Customer Name"
                name="customerName"
                value={newProduct.customerName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Order Date"
                name="orderDate"
                type="date"
                value={newProduct.orderDate}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />

              {/* Tracking Stop Sections */}
              {trackingStops.map((stop, index) => (
                <Box
                  key={index}
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Tracking Stop {index + 1}
                  </Typography>
                  <TextField
                    label="Activity"
                    value={stop.activity}
                    onChange={(e) =>
                      handleTrackingStopInputChange(
                        index,
                        "activity",
                        e.target.value
                      )
                    }
                    fullWidth
                    margin="normal"
                    placeholder="e.g., Package Picked Up"
                  />
                  <TextField
                    label="Date & Time"
                    type="datetime-local"
                    value={
                      stop.datetime ? formatDateTimeForInput(stop.datetime) : ""
                    }
                    onChange={(e) =>
                      handleTrackingStopInputChange(
                        index,
                        "datetime",
                        formatDateTimeForDisplay(e.target.value)
                      )
                    }
                    fullWidth
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Location"
                    value={stop.location}
                    onChange={(e) =>
                      handleTrackingStopInputChange(
                        index,
                        "location",
                        e.target.value
                      )
                    }
                    fullWidth
                    margin="normal"
                    placeholder="e.g., New York, NY"
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleRemoveTrackingStop(index)}
                    sx={{ mt: 1 }}
                  >
                    Remove Stop
                  </Button>
                </Box>
              ))}

              {/* Add Tracking Stop Button */}
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleAddTrackingStop}
                  fullWidth
                  startIcon={
                    <IconButton sx={{ color: "success.dark" }}>
                      <Icon icon="eva:plus-fill" width={14} height={14} />
                    </IconButton>
                  }
                  sx={{ color: "success.dark", textTransform: "none" }}
                >
                  Add Stop
                </Button>
              </Box>

              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleAddProduct}
                  fullWidth
                >
                  Add Shipping
                </Button>
                <Button variant="outlined" onClick={handleCloseModal} fullWidth>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>
      </Page>
    </div>
  );
}
